import axios from 'axios';
import { toast } from 'sonner';
// Phase W0 — shared host→slug resolution (handles white-label apexes too).
import { getTenantSlug } from './tenantHost';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Friendly auth-expired UX. Two reasons we override what the server sends:
//   1) FastAPI's HTTPBearer default detail is "Not authenticated" — meaningless
//      to a non-technical end user. They see this and wonder what to do.
//   2) Even when our backend sets a clearer detail ("Session expired"), the
//      individual callers' catch handlers display err.response.data.detail
//      which can vary. We normalise to one consistent message everywhere.
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again to continue.';
let _sessionExpiredHandled = false;  // de-dupe — one toast + one redirect, not N

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const slug = getTenantSlug();
  if (slug) {
    config.headers['X-Tenant-Slug'] = slug;
  }
  return config;
});

// ── 429 backoff ──────────────────────────────────────────────────────────────
// Rapid browser back/forward re-fires every request on every page (the
// Accounting dashboard alone costs ~13 GETs on mount), which used to surface a
// raw "too many requests" error toast to a user who had done nothing wrong.
//
// The server now tells us exactly how long to wait (Retry-After) and how much
// budget is left (X-RateLimit-Remaining). We retry ONCE, quietly, after that
// delay plus jitter — jitter so N parallel requests that were throttled
// together don't all return at the same instant and throttle each other again.
// Only idempotent reads are retried: silently re-sending a POST could double a
// payment. Writes surface a clear, actionable message instead.
const _IDEMPOTENT = new Set(['get', 'head', 'options']);
const _sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 429 && !error.config?._retried429) {
      const cfg = error.config || {};
      const retryAfter = Number(error.response.headers?.['retry-after']);
      const waitMs = (Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 5) * 1000;
      if (_IDEMPOTENT.has((cfg.method || 'get').toLowerCase()) && waitMs <= 15000) {
        cfg._retried429 = true;
        await _sleep(waitMs + Math.floor(Math.random() * 400));
        return api(cfg);
      }
      if (error.response.data && typeof error.response.data === 'object') {
        error.response.data.detail =
          'The server is limiting requests right now. Please wait a few seconds and try again.';
      }
    }
    return Promise.reject(error);
  }
);

// 403 — "you're signed in, but this isn't yours to do."
//
// Most routers raise a bare "Not authorized", which is developer shorthand
// and reads to a user as though something broke. Roles & Access writes a
// proper sentence ("Your role does not include permission to delete leads…"),
// and THAT must survive — so this only upgrades the terse defaults and never
// overwrites a message someone deliberately wrote.
const TERSE_403 = new Set([
  '', 'not authorized', 'forbidden', 'not authenticated', 'access denied',
  'permission denied', 'not allowed',
]);
const FRIENDLY_403 =
  "Sorry — you don't have access to this. If you think you should, ask an "
  + 'admin to enable it for your role in Settings → Roles & Access.';

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      const data = error.response.data;
      if (data && typeof data === 'object') {
        const detail = typeof data.detail === 'string' ? data.detail : '';
        if (TERSE_403.has(detail.trim().toLowerCase().replace(/[.!]$/, ''))) {
          data.detail = FRIENDLY_403;
        }
      } else if (data == null || typeof data === 'string') {
        error.response.data = { detail: FRIENDLY_403 };
      }
    }
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    // A 401 from a credential submission (login/register) means "wrong
    // credentials", NOT an expired session — let it propagate with the
    // server's real detail ("Invalid credentials") so the form shows that,
    // instead of hijacking it into the session-expired toast + redirect.
    // (`/auth/me` and every other 401 still mean the session went away.)
    const reqUrl = error.config?.url || '';
    const isCredentialAttempt = /\/auth\/(login|register)\b/.test(reqUrl);
    if (error.response?.status === 401 && !isCredentialAttempt) {
      // Override the server-supplied detail so downstream catch handlers
      // (which display err.response.data.detail) all show the friendly
      // message instead of FastAPI's "Not authenticated" default.
      if (error.response.data && typeof error.response.data === 'object') {
        error.response.data.detail = SESSION_EXPIRED_MESSAGE;
      }
      // De-dupe: if the page fires 5 parallel requests after the token
      // expired, we'd otherwise show 5 toasts and start 5 redirects.
      if (!_sessionExpiredHandled) {
        _sessionExpiredHandled = true;
        toast.error(SESSION_EXPIRED_MESSAGE, { duration: 4000 });
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // A demo-domain override (Switch Domain) is only valid alongside the
        // token the backend minted for that tenant. Once the token is gone the
        // override is stale — leaving it made the login page silently target
        // the switched-to tenant ("Invalid credentials", wrong branding).
        try { localStorage.removeItem('demo_tenant_slug'); } catch { /* private mode */ }
        // Small delay lets the user actually read the toast before the
        // page navigates away. Login page also gets a query param so it
        // can show a banner if we want to surface "you were logged out".
        setTimeout(() => {
          window.location.href = '/login?reason=session_expired';
        }, 1200);
      }
    }
    return Promise.reject(error);
  }
);

// File utilities — direct anchor-tag navigation (no fetch, no blob URL).
//
// Why no fetch: cross-origin fetch() requires the target server to send
// CORS headers. R2 buckets often don't, which made the previous fetch+blob
// approach throw "Failed to load document" intermittently. Plain <a> tags
// (target=_blank for view, download=... for download) load cross-origin
// resources natively without any CORS requirement — same way <img src=>
// and <iframe src=> work.
//
// file_url shape: since the R2 migration (2026-04-26) the backend stores
// absolute URLs (https://pub-xxx.r2.dev/immiways/foo.pdf). Legacy uploads
// have relative `/api/files/...` paths. Both keep working.
function _resolveFileUrl(fileUrl) {
  if (!fileUrl) return '';
  return /^https?:\/\//i.test(fileUrl) ? fileUrl : `${BACKEND_URL}${fileUrl}`;
}

function _clickAnchor(href, attrs) {
  const link = document.createElement('a');
  link.href = href;
  Object.entries(attrs || {}).forEach(([k, v]) => { link[k] = v; });
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export const fileUtils = {
  // Open in a new tab — browser handles content-type natively.
  viewFile: (fileUrl) => {
    const url = _resolveFileUrl(fileUrl);
    if (!url) return;
    _clickAnchor(url, { target: '_blank', rel: 'noopener noreferrer' });
  },

  // Trigger a REAL download.
  // PRIMARY (2026-07-22): stream the bytes through OUR backend as a blob and
  // save locally — R2's presigned/public endpoints returned intermittent
  // 503s on staff networks, so anchor-navigation downloads silently failed
  // for admins/case officers. A blob URL is same-origin, so the `download`
  // attribute is always honored and the save dialog always opens.
  // Fallbacks: presigned attachment URL, then legacy behavior.
  downloadFile: async (fileUrl, fileName) => {
    const url = _resolveFileUrl(fileUrl);
    if (!url) return;
    try {
      const res = await api.get('/documents/download-file', {
        params: { file_url: url, file_name: fileName || '' },
        responseType: 'blob',
        timeout: 120000,
      });
      const blobUrl = URL.createObjectURL(res.data);
      _clickAnchor(blobUrl, { download: fileName || url.split('/').pop() || 'download' });
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
      return;
    } catch { /* fall through — backend streaming unavailable */ }
    try {
      const { data } = await api.get('/documents/download-url', {
        params: { file_url: url, file_name: fileName || '' },
      });
      if (data?.download_url) {
        _clickAnchor(data.download_url, {});
        return;
      }
    } catch { /* fall through to legacy behavior */ }
    // Legacy /api/files/... paths — the server forces attachment via ?download=1.
    if (!/^https?:\/\//i.test(fileUrl) && fileUrl.includes('/files/')) {
      _clickAnchor(`${url}${url.includes('?') ? '&' : '?'}download=1`, {});
      return;
    }
    _clickAnchor(url, {
      target:   '_blank',
      rel:      'noopener noreferrer',
      download: fileName || 'download',
    });
  }
};

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  // Per-user answer-language preference (multilingual Ask Euphoric v1).
  setLanguage: (language) => api.patch('/auth/me/language', { language }),
};

export const userAPI = {
  getUsers: () => api.get('/users'),
  createUser: (userData) => api.post('/users', userData),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  updateUser: (userId, userData) => api.put(`/users/${userId}`, userData),
};

export const orgAPI = {
  getManagerOptions: (staffId) =>
    api.get(`/org/manager-options${staffId ? `?staff_id=${encodeURIComponent(staffId)}` : ''}`),
  getDescendants: (staffId) => api.get(`/org/${encodeURIComponent(staffId)}/descendants`),
};

export const salesTargetsAPI = {
  // Assign a new target. Auto-supersedes any active target of the same metric
  // whose period overlaps with the new one.
  assignTarget: (data) => api.post('/sales/targets', data),

  // Active target covering today's date for staff_id + metric. Returns { target: null }
  // when no target is set for the current period.
  getCurrent: (staffId, metric = 'revenue') =>
    api.get(`/sales/targets/${encodeURIComponent(staffId)}/current?metric=${encodeURIComponent(metric)}`),

  // Full append-only history for one staff member, newest first.
  getHistory: (staffId) => api.get(`/sales/targets/${encodeURIComponent(staffId)}/history`),

  // Current active targets for every direct + indirect report of manager_id.
  // Manager dashboard uses this to render the whole team in one round-trip.
  getTeamTargets: (managerId, metric = 'revenue') =>
    api.get(`/sales/targets/team/${encodeURIComponent(managerId)}?metric=${encodeURIComponent(metric)}`),

  // Full Team Dashboard payload — per-member target + achievement + pipeline,
  // plus team-wide aggregate. Single round-trip for SalesManagerDashboard.
  getTeamOverview: (managerId, metric = 'revenue') =>
    api.get(`/sales/targets/team-overview/${encodeURIComponent(managerId)}?metric=${encodeURIComponent(metric)}`),

  // Personal scoreboard for the logged-in consultant: target, achievement,
  // pipeline, deals won, recent wins. Powers the SalesDashboard hero.
  getMySnapshot: (metric = 'revenue') =>
    api.get(`/sales/targets/my-snapshot?metric=${encodeURIComponent(metric)}`),
};

// HR · Job Applications — the careers and internships forms land here.
// Rows are created by the website, never from the OS, so there is no create().
export const jobApplicationsAPI = {
  list:       (params)        => api.get('/job-applications', { params }),
  counts:     ()              => api.get('/job-applications/counts'),
  get:        (id)            => api.get(`/job-applications/${id}`),
  // disposition: 'inline' previews in an iframe, 'attachment' prompts a save.
  resumeUrl:  (id, disposition = 'attachment') =>
    api.get(`/job-applications/${id}/resume-url`, { params: { disposition } }),
  update:     (id, data)      => api.patch(`/job-applications/${id}`, data),
  reply:      (id, data)      => api.post(`/job-applications/${id}/reply`, data),
  // One-click import of applications that predate this module (super admin).
  backfill:   ()              => api.post('/job-applications/backfill'),
};

export const staffAPI = {
  getStaff:      ()           => api.get('/users/staff'),
  createStaff:   (data)       => api.post('/users/staff', data),
  updateStaff:   (id, data)   => api.put(`/users/staff/${id}`, data),
  deleteStaff:   (id)         => api.delete(`/users/${id}`),
  resetPassword: (id)         => api.post(`/users/staff/${id}/reset-password`),
  // Enable / disable without destroying the record. Deleting a staff member
  // takes their attendance, payroll and audit trail with them; deactivating
  // keeps all of it and simply stops them signing in.
  setActive:     (id, is_active, reason) =>
    api.patch(`/users/staff/${id}/active`, { is_active, reason: reason || null }),
};

export const designationAPI = {
  getDesignations: () => api.get('/users/designations'),
  createDesignation: (data) => api.post('/users/designations', data),
  updateDesignation: (id, data) => api.put(`/users/designations/${id}`, data),
  deleteDesignation: (id) => api.delete(`/users/designations/${id}`),
};

// Roles & Access — the tenant's own role names + the permission matrix.
// `me` is what the UI gates nav on, so a trimmed role sees a smaller sidebar
// instead of pages that load and then 403 into emptiness.
export const permissionSetAPI = {
  list:     () => api.get('/permission-sets'),
  catalog:  () => api.get('/permission-sets/catalog'),
  me:       () => api.get('/permission-sets/me'),
  create:   (data) => api.post('/permission-sets', data),
  update:   (id, data) => api.patch(`/permission-sets/${id}`, data),
  remove:   (id) => api.delete(`/permission-sets/${id}`),
};

// Data Export — the firm downloads its own records.
//
// Fetched through the API client rather than by navigating to the URL,
// because the endpoint is authenticated and a plain <a href> carries no
// Authorization header. The response is streamed on the SERVER (peak memory
// there is one batch, which is the constraint that matters on a 512MB box);
// the browser buffering a CSV is comfortably fine.
export const dataExportAPI = {
  datasets: () => api.get('/data-export/datasets'),
  download: async (dataset, params = {}) => {
    const clean = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== '' && v != null)
    );
    const res = await api.get(`/data-export/${dataset}`, {
      params: clean,
      responseType: 'blob',
    });
    const disposition = res.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
    _clickAnchor(url, { download: match ? match[1] : `${dataset}.csv` });
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },
};

export const contractorPaymentAPI = {
  create: (data) => api.post('/contractor-payments', data),
  getAll: (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return api.get(`/contractor-payments${qs ? `?${qs}` : ''}`);
  },
  getSummary: () => api.get('/contractor-payments/summary'),
  // Cases assigned to a per-case contractor that carry no fee record — the
  // invariant check that catches assign-succeeded-but-fee-never-logged.
  getUnlogged: () => api.get('/contractor-payments/unlogged'),
  reassignPreview: (caseId, newOfficerId) =>
    api.get(`/contractor-payments/reassign-preview?case_id=${encodeURIComponent(caseId)}&new_officer_id=${encodeURIComponent(newOfficerId)}`),
  reassignCase: (data) => api.post('/contractor-payments/reassign-case', data),
  backfillExpenseFields: () => api.post('/contractor-payments/backfill-expense-fields'),
  markPaid: (id, data) => api.patch(`/contractor-payments/${id}/mark-paid`, data),
  markUnpaid: (id) => api.patch(`/contractor-payments/${id}/mark-unpaid`),
  waive: (id, data = {}) => api.patch(`/contractor-payments/${id}/waive`, data),
};

export const clientAPI = {
  getClients: () => api.get('/clients'),
  createClient: (clientData) => api.post('/clients', clientData),
  getClient: (id) => api.get(`/clients/${id}`),
  updateClient: (id, data) => api.put(`/clients/${id}`, data),
  createCaseForClient: (clientId, data) => api.post(`/clients/${clientId}/create-case`, data),
  getClientCase: (clientId) => api.get(`/cases?client_id=${clientId}`),
  resetPortalPassword: (clientId) => api.post(`/clients/${clientId}/reset-portal-password`),
  // Rotates the portal password and re-sends the credentials/welcome email
  // to the client's CURRENT address (the wrong-email-at-cascade recovery).
  resendWelcome: (clientId) => api.post(`/clients/${clientId}/resend-welcome`),
  // Re-sends EVERY stage-appropriate client email (welcome+credentials,
  // payment receipt, signed-agreement copy, officer intro, doc reminder).
  resendLifecycleEmails: (clientId) => api.post(`/clients/${clientId}/resend-lifecycle-emails`),
};

export const caseAPI = {
  // params: { scope?: 'mine' | 'team' } — process_manager Me/Team toggle.
  // Other roles ignore scope server-side.
  getCases: (params = {}) => api.get('/cases', { params }),
  getCase: (id) => api.get(`/cases/${id}`),
  getMyCases: () => api.get('/cases'),  // Returns filtered cases for the logged-in user
  createCase: (caseData) => api.post('/cases', caseData),
  updateCase: (id, data) => api.patch(`/cases/${id}`, data),
  changeVisaProgram: (caseId, visaProgramId) => api.put(`/cases/${caseId}/change-visa-program?visa_program_id=${visaProgramId}`),
  // Decision + filing. These advance the ProcessStage chain and notify the
  // client but do NOT write `stage` — the board's confirm modals call them
  // first, then updateCase({stage}) so the Kanban column matches.
  // NOTE: decision is a QUERY param (backend signature is a bare str).
  updateVisaDecision: (id, decision) =>
    api.post(`/cases/${id}/update-visa-decision`, null, { params: { decision } }),
  fileApplication: (id) => api.post(`/cases/${id}/file-application`),
  // Case follow-ups (routers/case_followups.py) — mirrors leadsAPI's My Day
  // block. params: { mine?: bool, assigned_to?: string }.
  overdueFollowUps: (params = {}) => api.get('/cases/follow-ups/due', { params }),
  todayFollowUps: (params = {}) => api.get('/cases/follow-ups/today', { params }),
  upcomingWeekFollowUps: (params = {}) => api.get('/cases/follow-ups/upcoming-week', { params }),
  scheduleFollowUp: (id, body) => api.post(`/cases/${id}/follow-up`, body),
  followUpDone: (id, body) => api.post(`/cases/${id}/follow-up/done`, body),
  followUpSnooze: (id, snoozeUntil) =>
    api.post(`/cases/${id}/follow-up/snooze`, { snooze_until: snoozeUntil }),
  // Pending/rejected doc count per visible case → board card badges.
  pipelineDocCounts: () => api.get('/cases/pipeline/doc-counts'),
};

// Sale Payments — the balance-payment recording stream. The consultant who
// closed the lead records each balance payment; ONLINE methods auto-confirm,
// OFFLINE methods land pending accountant verification before the client email.
// Payment methods: bank_transfer, card, cash, upi, cheque, online, wire, other.
export const salePaymentsAPI = {
  list:   (saleId) => api.get(`/sales-records/${saleId}/payments`),
  record: (saleId, body) => api.post(`/sales-records/${saleId}/payments`, body),
  void:   (saleId, paymentId, void_reason) =>
    api.post(`/sales-records/${saleId}/payments/${paymentId}/void`, { void_reason }),
  // Accountant verification of offline balance payments.
  listPending: () => api.get('/sales-records/payments/pending'),
  approve: (saleId, paymentId) =>
    api.post(`/sales-records/${saleId}/payments/${paymentId}/approve`),
  reject:  (saleId, paymentId, void_reason) =>
    api.post(`/sales-records/${saleId}/payments/${paymentId}/reject`, { void_reason }),
};

// Sales Reports — Salesforce-grade aggregations. Backend role-scopes
// automatically (consultant→own, manager→team, admin/super_admin→company),
// and `scope` may override (auto | mine | team | company). Consultants are
// locked to "mine" regardless of what they ask for.
export const salesReportsAPI = {
  overview: ({ days = 30, startDate, endDate, scope = 'auto' } = {}) => {
    const qs = new URLSearchParams();
    if (days) qs.set('days', String(days));
    if (startDate) qs.set('start_date', startDate);
    if (endDate) qs.set('end_date', endDate);
    if (scope) qs.set('scope', scope);
    return api.get(`/sales/reports/overview?${qs.toString()}`);
  },
  // Collections lens — cash received windowed by PAYMENT date (a July
  // balance payment on a June sale credits July, flagged as a recovery).
  // Same scoping as overview. This is the incentive basis.
  collections: ({ days = 30, startDate, endDate, scope = 'auto' } = {}) => {
    const qs = new URLSearchParams();
    if (days) qs.set('days', String(days));
    if (startDate) qs.set('start_date', startDate);
    if (endDate) qs.set('end_date', endDate);
    if (scope) qs.set('scope', scope);
    return api.get(`/sales/reports/collections?${qs.toString()}`);
  },
  // Per-client breakdown behind the Booked KPI (drill-down). Same scoping.
  salesBreakdown: ({ days = 30, startDate, endDate, scope = 'auto' } = {}) => {
    const qs = new URLSearchParams();
    if (days) qs.set('days', String(days));
    if (startDate) qs.set('start_date', startDate);
    if (endDate) qs.set('end_date', endDate);
    if (scope) qs.set('scope', scope);
    return api.get(`/sales/reports/sales-breakdown?${qs.toString()}`);
  },
  // Marketing-performance report over the cohort of leads ASSIGNED in the
  // period — distribution, working discipline, funnel, velocity, segmentation.
  // Powers /admin/marketing/assigned-leads. assignedTo drills into one rep.
  assignedLeads: ({ days = 30, startDate, endDate, assignedTo } = {}) => {
    const qs = new URLSearchParams();
    if (days) qs.set('days', String(days));
    if (startDate) qs.set('start_date', startDate);
    if (endDate) qs.set('end_date', endDate);
    if (assignedTo) qs.set('assigned_to', assignedTo);
    return api.get(`/sales/reports/assigned-leads?${qs.toString()}`);
  },
};

// Tenant-configurable lead-intake taxonomies (Country of Interest + Visa
// Type). Admin/super_admin manage them from /admin/lead-intake-settings;
// lead forms read the active set via listActive(). `kind` is one of:
//   • 'destinations' — countries the tenant accepts enquiries for
//   • 'visa-types'   — visa types the tenant offers
export const leadTaxonomyAPI = {
  listActive: (kind) => api.get(`/lead-intake/${kind}`),
  listAll:    (kind) => api.get(`/lead-intake/${kind}/all`),
  create:     (kind, name) => api.post(`/lead-intake/${kind}`, { name }),
  update:     (kind, id, body) => api.patch(`/lead-intake/${kind}/${id}`, body),
  remove:     (kind, id) => api.delete(`/lead-intake/${kind}/${id}`),
};

export const visaProgramAPI = {
  getPrograms: () => api.get('/visa-programs'),
  getProgram: (id) => api.get(`/visa-programs/${id}`),
  createProgram: (data) => api.post('/visa-programs', data),
  updateProgram: (id, data) => api.put(`/visa-programs/${id}`, data),
  deleteProgram: (id) => api.delete(`/visa-programs/${id}`),
  // Phase 1.9.3 Checkpoint B — convert an uploaded .docx to editable HTML for
  // the template editor (does not persist; returns { html, messages }).
  importAgreementDocx: (id, formData) => api.post(`/visa-programs/${id}/import-agreement`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const checklistAPI = {
  getChecklistByProgram: (programId) => api.get(`/checklist-templates/visa-program/${programId}`),
  createTemplate: (data) => api.post('/checklist-templates', data),
  getAllTemplates: () => api.get('/checklist-templates'),
};

export const documentAPI = {
  getCaseDocuments: (caseId) => api.get(`/documents/case/${caseId}`),
  getByCaseId: (caseId) => api.get(`/documents/case/${caseId}`),  // Alias for compatibility
  uploadDocument: (data) => api.post('/documents', data),
  uploadDocumentWithFile: (formData) => api.post('/documents/upload-file', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateDocument: (id, data) => api.patch(`/documents/${id}`, data),
  getDocument: (id) => api.get(`/documents/${id}`),
  clientReview: (id, data) => api.post(`/documents/${id}/client-review`, data),
};

export const legalAPI = {
  // Escalations
  createEscalation:   (data)           => api.post('/legal/escalations', data),
  listEscalations:    (params = {})    => api.get('/legal/escalations', { params }),
  getEscalation:      (id)             => api.get(`/legal/escalations/${id}`),
  updateEscalation:   (id, data)       => api.patch(`/legal/escalations/${id}`, data),
  // Notes
  addNote:            (id, data)       => api.post(`/legal/escalations/${id}/notes`, data),
  getNotes:           (id)             => api.get(`/legal/escalations/${id}/notes`),
  getEvidence:        (id)             => api.get(`/legal/escalations/${id}/evidence`),
  // Analytics
  getAnalytics:       (days = 90)      => api.get('/legal/analytics', { params: { days } }),
};

export const completeFileAPI = {
  get:              (caseId)        => api.get(`/cases/${caseId}/complete-file`),
  generate:         (caseId)        => api.post(`/cases/${caseId}/complete-file/generate`),
  sendToClient:     (caseId)        => api.post(`/cases/${caseId}/complete-file/send-to-client`),
  uploadGovForm:    (caseId, fd)    => api.post(`/cases/${caseId}/complete-file/government-forms`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteGovForm:    (caseId, fmId)  => api.delete(`/cases/${caseId}/complete-file/government-forms/${fmId}`),
  approve:          (caseId, data)  => api.post(`/cases/${caseId}/complete-file/approve`, data),
  reject:           (caseId, data)  => api.post(`/cases/${caseId}/complete-file/reject`, data),
  getLegalRecord:   (caseId)        => api.get(`/cases/${caseId}/complete-file/legal-record`),
};

export const familyAPI = {
  // Family composition
  updateComposition: (caseId, data) => api.patch(`/cases/${caseId}/family-composition`, data),

  // Family members
  addMember: (caseId, data) => api.post(`/cases/${caseId}/family-members`, data),
  updateMember: (caseId, memberId, data) => api.patch(`/cases/${caseId}/family-members/${memberId}`, data),
  deleteMember: (caseId, memberId) => api.delete(`/cases/${caseId}/family-members/${memberId}`),

  // Document summary (per-applicant progress)
  getDocumentSummary: (caseId) => api.get(`/cases/${caseId}/document-summary`),

  // Per-case checklist waivers — mark one product-checklist item Not
  // Applicable for THIS client. Waived, never deleted.
  waiveChecklistItem: (caseId, data) => api.post(`/cases/${caseId}/checklist-waivers`, data),
  unwaiveChecklistItem: (caseId, itemName) =>
    api.delete(`/cases/${caseId}/checklist-waivers/${encodeURIComponent(itemName)}`),

  // Custom document requests (officer → client)
  getCustomRequests: (caseId) => api.get(`/cases/${caseId}/custom-document-requests`),
  addCustomRequest: (caseId, data) => api.post(`/cases/${caseId}/custom-document-requests`, data),
  deleteCustomRequest: (caseId, requestId) => api.delete(`/cases/${caseId}/custom-document-requests/${requestId}`),
};

export const sampleDocAPI = {
  getCaseSamples: (caseId) => api.get(`/sample-documents/case/${caseId}`),
  uploadSample: (formData) => api.post('/sample-documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const fileAPI = {
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFileUrl: (filename) => `${API_BASE}/files/${filename}`,
};

export const b2bPartnerAPI = {
  getPartners: () => api.get('/b2b-partners'),
  getPartner: (id) => api.get(`/b2b-partners/${id}`),
  createPartner: (data) => api.post('/b2b-partners', data),
  updatePartner: (id, data) => api.put(`/b2b-partners/${id}`, data),
  deletePartner: (id) => api.delete(`/b2b-partners/${id}`),
  // Portal logins — issuing credentials is a separate action from creating
  // the partner row, because the welcome email carries a password.
  enablePortal: (id) => api.post(`/b2b-partners/${id}/enable-portal`),
  disablePortal: (id) => api.post(`/b2b-partners/${id}/disable-portal`),
};

// The partner's own view of their book. Every endpoint resolves the caller's
// partner id server-side — none of them accept one from the client, which is
// what keeps partner A out of partner B's cases.
export const partnerPortalAPI = {
  getMe: () => api.get('/partner/me'),
  getBook: () => api.get('/partner/book'),
  getLadder: (caseId) => api.get(`/partner/cases/${caseId}/ladder`),
  getNotes:  (caseId) => api.get(`/partner/cases/${caseId}/notes`),
  addNote:   (caseId, note) => api.post(`/partner/cases/${caseId}/notes`, { note }),
};

export const activityAPI = {
  getCaseTimeline: (caseId) => api.get(`/activity-logs/case/${caseId}`),
  // Phase 1.9.3 #10 — downloadable CSV evidence pack of the case timeline.
  exportCaseLog: (caseId) => api.get(`/activity-logs/case/${caseId}/export`, { responseType: 'blob' }),
};

export const agreementAPI = {
  // Pre-existing case-bound agreement surfaces (Zoho Sign flow).
  getByCase: (caseId) => api.get(`/agreements/case/${caseId}`),
  sign: (caseId, data) => api.post(`/agreements/case/${caseId}/sign`, data),
  // Phase 1.9 — locked-template preview surfaces.
  // previewStaged: pre-sale render driven by RegisterLeadModal Step 4.
  // preview/previewPdf: post-sale render (Phase 1.9g/h signing artifacts).
  previewStaged: (lead_id, registration) =>
    api.post('/agreements/preview-staged', { lead_id, registration }),
  preview:    (sale_id) => api.get(`/agreements/preview/${sale_id}`),
  previewPdf: (sale_id) => api.get(`/agreements/preview/${sale_id}/pdf`, { responseType: 'blob' }),
  // Phase 1.9e — client portal surfaces.
  // previewByCase: read-only agreement render for the "Review & Sign" CTA.
  // getPortalTerms/acceptPortalTerms: first-login hard T&C gate.
  previewByCase:    (case_id) => api.get(`/agreements/case/${case_id}/preview`),
  // signingStatusByCase: single source of truth for the portal's
  // document-gate. requires_signing is true ONLY for a modern signable
  // agreement (a sales_records row exists) that is unsigned — legacy
  // clients (no sales_records row) are correctly ungated.
  signingStatusByCase: (case_id) => api.get(`/agreements/case/${case_id}/signing-status`),
  getPortalTerms:   () => api.get('/agreements/portal/terms'),
  acceptPortalTerms: () => api.post('/agreements/portal/terms/accept'),
  // Phase 1.9g — client click-wrap signature.
  signClickwrap:    (case_id, data) => api.post(`/agreements/case/${case_id}/sign-clickwrap`, data),
  // Phase 1.9j — post-signing profile completion wizard.
  getPortalProfile:  () => api.get('/agreements/portal/profile'),
  savePortalProfile: (data) => api.put('/agreements/portal/profile', data),
};

export const notificationAPI = {
  getNotifications: (unreadOnly = false) => api.get(`/notifications?unread_only=${unreadOnly}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.patch(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.patch('/notifications/read-all'),
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),
  clearAll: () => api.delete('/notifications/clear-all'),
};

export const analyticsAPI = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getFiltered: (days = 30, startDate = null, endDate = null) => {
    let url = `/analytics/dashboard-filtered?days=${days}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    return api.get(url);
  },
  // Processing Overview — KPI tiles, officer leaderboard, stage funnel.
  // params: { scope?: 'mine' | 'team' } — player-coach toggle.
  getProcessingOverview: (params = {}) => api.get('/analytics/processing-overview', { params }),
  getSuccessRatio: (days = 30, startDate = null, endDate = null, officerId = null) => {
    let url = `/analytics/success-ratio?days=${days}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    if (officerId) {
      url += `&officer_id=${officerId}`;
    }
    return api.get(url);
  },
};

// Financier Submissions (Praxia F1) — multi-bank status board on a deal.
export const financierAPI = {
  list: (caseId) => api.get(`/cases/${caseId}/financier-submissions`),
  add: (caseId, data) => api.post(`/cases/${caseId}/financier-submissions`, data),
  update: (caseId, subId, data) => api.patch(`/cases/${caseId}/financier-submissions/${subId}`, data),
  remove: (caseId, subId) => api.delete(`/cases/${caseId}/financier-submissions/${subId}`),
  select: (caseId, subId) => api.post(`/cases/${caseId}/financier-submissions/${subId}/select`),
  // Disbursement — the record the finance controller books once the winning
  // financier has actually released the funds. Last step of the deal.
  getDisbursement: (caseId) => api.get(`/cases/${caseId}/disbursement`),
  recordDisbursement: (caseId, data) => api.post(`/cases/${caseId}/disbursement`, data),
  clearDisbursement: (caseId) => api.delete(`/cases/${caseId}/disbursement`),
};

export const complaintAPI = {
  getComplaints: () => api.get('/complaints'),
  // Unresolved count for the red sidebar badge (role-scoped server-side).
  getOpenCount: () => api.get('/complaints/open-count'),
  getComplaint: (id) => api.get(`/complaints/${id}`),
  createComplaint: (data) => api.post('/complaints', data),
  updateComplaint: (id, data) => api.patch(`/complaints/${id}`, data),
  getStats: (days = 30, startDate = null, endDate = null) => {
    let url = `/complaints/stats?days=${days}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    return api.get(url);
  },
  // Complaint Natures (Types) CRUD
  getNatures: () => api.get('/complaint-natures'),
  getAllNatures: () => api.get('/complaint-natures/all'),
  createNature: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    return api.post('/complaint-natures', formData);
  },
  updateNature: (id, data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('is_active', data.is_active);
    return api.put(`/complaint-natures/${id}`, formData);
  },
  deleteNature: (id) => api.delete(`/complaint-natures/${id}`),
};

export const caseServiceAPI = {
  extendService: (caseId, months) => api.post(`/cases/${caseId}/extend?extension_months=${months}`),
  getTimeRemaining: (caseId) => api.get(`/cases/${caseId}/time-remaining`),
  getProcessStages: (caseId) => api.get(`/cases/${caseId}/process-stages`),
  // Phase 3 — per-case timeline extension. Pushes service tenure and/or
  // document upload deadline forward by a number of days with a required
  // reason. Backend rejects negative values (extension only) and requires
  // at least one of the two day counts to be > 0.
  extendTimeline: (caseId, payload) => api.post(`/cases/${caseId}/extend-timeline`, payload),
  getTimelineAdjustments: (caseId) => api.get(`/cases/${caseId}/timeline-adjustments`),
  // Phase 5 — lift a barred case back to active with a new service window
  // and a required reason. Roles: super_admin / admin / process_manager.
  reactivate: (caseId, payload) => api.post(`/cases/${caseId}/reactivate`, payload),
};

export const csatAPI = {
  submitRating: (caseId, rating, feedback) => api.post(`/csat/submit?case_id=${caseId}&rating=${rating}&feedback=${feedback}`),
  getOfficerCSAT: (officerId, days = 30) => api.get(`/csat/officer/${officerId}?days=${days}`),
  getCompanyCSAT: (days = 30) => api.get(`/csat/company?days=${days}`),
  getMyCSAT: (days = 30) => api.get(`/csat/my-score?days=${days}`),
};

// Email Service APIs
export const emailAPI = {
  // Email logs (original)
  getLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/email-logs?${queryParams.toString()}`);
  },
  getStats: () => api.get('/email-logs/stats'),
  retryEmail: (emailId) => api.post(`/email-logs/${emailId}/retry`),
  processQueue: (batchSize = 5) => api.post(`/email-queue/process?batch_size=${batchSize}`),
  getTemplates: () => api.get('/email-templates'),
  getTemplate: (type) => api.get(`/email-templates/${type}`),
  saveTemplate: (data) => api.post('/email-templates', data),
  deleteTemplate: (type) => api.delete(`/email-templates/${type}`),
  triggerReminders: () => api.post('/cases/reminders'),
  
  // Email module (new)
  getEmails: (folder, caseId) => api.get(`/emails?folder=${folder}${caseId ? `&case_id=${caseId}` : ''}`),
  getEmailDetail: (emailId) => api.get(`/emails/${emailId}`),
  sendEmail: (data) => {
    const formData = new FormData();
    formData.append('to_email', data.to_email);
    formData.append('subject', data.subject);
    formData.append('body', data.body);
    if (data.case_id) formData.append('case_id', data.case_id);
    if (data.client_id) formData.append('client_id', data.client_id);
    // Correspondence with a lead who may never become a case.
    if (data.lead_id) formData.append('lead_id', data.lead_id);
    // Support for attachments
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }
    return api.post('/emails/compose', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  saveDraft: (data) => {
    const formData = new FormData();
    if (data.to_email) formData.append('to_email', data.to_email);
    if (data.subject) formData.append('subject', data.subject);
    if (data.body) formData.append('body', data.body);
    if (data.case_id) formData.append('case_id', data.case_id);
    if (data.client_id) formData.append('client_id', data.client_id);
    if (data.draft_id) formData.append('draft_id', data.draft_id);
    return api.post('/emails/draft', formData);
  },
  deleteDraft: (draftId) => api.delete(`/emails/draft/${draftId}`),
  getUnreadCount: () => api.get('/emails/unread-count'),

  // Lifecycle + manual email audit log on a case. New endpoint reads from
  // db.email_logs (one source of truth, includes status_history, message_id,
  // delivered/opened/bounced webhook events, reason_text). Replaces the
  // narrower /emails/case/{id} view for the case-detail Emails tab.
  getCaseEmailLog:    (caseId) => api.get(`/cases/${caseId}/emails`),
  // Every email about a client, across all their cases — the client
  // record is the only place a person exists continuously.
  getClientEmails:    (clientId) => api.get(`/clients/${clientId}/emails`),
  getCaseEmailDetail: (caseId, emailId) => api.get(`/cases/${caseId}/emails/${emailId}`),

  // My own mailbox (email Phase A). Lives on /profile, NOT admin Settings:
  // officers and consultants are the people who email clients and cannot
  // open Settings at all.
  getMyMailbox:      () => api.get('/me/mail'),
  startMailConnect:  () => api.post('/me/mail/oauth/start'),
  disconnectMailbox: () => api.delete('/me/mail'),
  getMailboxRoster:  () => api.get('/me/mail/roster'),
  // Phase B — replies coming back in.
  pollMyMailbox:     () => api.post('/me/mail/poll'),
  getUnmatched:      () => api.get('/me/mail/unmatched'),
  assignUnmatched:   (emailId, target) => api.post(`/me/mail/unmatched/${emailId}/assign`, target),
  archiveEmail:      (emailId) => api.post(`/me/mail/${emailId}/archive`),
  unarchiveEmail:    (emailId) => api.post(`/me/mail/${emailId}/unarchive`),
  deleteEmail:       (emailId) => api.delete(`/me/mail/${emailId}`),
  restoreEmail:      (emailId) => api.post(`/me/mail/${emailId}/restore`),
  setEmailRead:      (emailId, isRead) => api.post(`/me/mail/${emailId}/read`, { is_read: isRead }),
  markAllRead:       () => api.post('/me/mail/read-all'),

  // Provider gating — whether outbound user-composed email is wired
  getProviderStatus: () => api.get('/email/provider-status'),
  notifyAdminToConfigure: () => api.post('/email/notify-admin-to-configure'),
};

// Zoho Sign APIs
export const signAPI = {
  getStatus: () => api.get('/zoho-sign/status'),
  sendForSigning: (caseId) => api.post(`/cases/${caseId}/send-agreement-for-signing`),
  getCaseRequests: (caseId) => api.get(`/sign-requests/case/${caseId}`),
  getRequestStatus: (requestId) => api.get(`/sign-requests/${requestId}/status`),
  sendReminder: (requestId) => api.post(`/sign-requests/${requestId}/remind`),
  recallRequest: (requestId) => api.post(`/sign-requests/${requestId}/recall`),
  downloadSigned: (requestId) => api.get(`/sign-requests/${requestId}/download`, { responseType: 'blob' }),
};

// Case Timeline & Notes APIs
export const timelineAPI = {
  getTimeline: (caseId) => api.get(`/cases/${caseId}/timeline`),
};

export const aiCaseAPI = {
  getInsights:    (caseId) => api.get(`/cases/ai/insights/${caseId}`),
  getTeamHealth:  ()       => api.get('/cases/ai/team-health'),
};

export const claudeAPI = {
  draftRejectionEmail: (docName, officerNote, clientName) =>
    api.post('/ai/rejection-email', { doc_name: docName, officer_note: officerNote, client_name: clientName }),
  auditFile:           (caseId) => api.post(`/ai/cases/${caseId}/audit-file`),
  draftSalesEmail:     (leadId, lastInteraction) =>
    api.post('/ai/sales/draft-email', { lead_id: leadId, last_interaction: lastInteraction }),
  callScript:          (leadId) => api.get(`/ai/sales/call-script/${leadId}`),
  chatAboutCase:       async (caseId, message, history, onChunk) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${BACKEND_URL}/api/ai/cases/${caseId}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ message, history }),
    });
    if (response.status === 402) {
      // Free-trial cap exhausted. Surface the structured upgrade payload
      // (used / limit / upgrade_contact) so the widget can render the
      // upgrade card with the founder's email instead of a generic error.
      let payload = {};
      try { payload = (await response.json()).detail || {}; } catch {}
      const err = new Error(payload.message || 'AI chat limit reached');
      err.code = 'ai_chat_limit_reached';
      err.payload = payload;
      throw err;
    }
    if (!response.ok) throw new Error('Chat request failed');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') return;
          try { onChunk(JSON.parse(data).text); } catch {}
        }
      }
    }
  },
};

export const notesAPI = {
  getNotes: (caseId) => api.get(`/cases/${caseId}/notes`),
  // visibility: 'internal' (default) keeps the note in the officers' private
  // thread; 'partner' addresses it TO the B2B partner and surfaces it in
  // their portal. Existing callers pass no third argument and stay internal.
  addNote: (caseId, note, visibility = 'internal') =>
    api.post(`/cases/${caseId}/notes`, { note, visibility }),
  deleteNote: (caseId, noteId) => api.delete(`/cases/${caseId}/notes/${noteId}`),
};

export const legalConsentAPI = {
  checkAcceptanceStatus: () => api.get('/legal/acceptance-status'),
  reaccept:              (documents) => api.post('/legal/reaccept', { documents }),
};

export const tasksAPI = {
  create:       (data) =>           api.post('/tasks', data),
  getMy:        (status) =>         api.get(`/tasks/my${status ? `?status=${status}` : ''}`),
  getTeam:      (params) =>         api.get('/tasks/team', { params }),
  getForCase:   (caseId) =>         api.get(`/tasks/case/${caseId}`),
  update:       (id, data) =>       api.patch(`/tasks/${id}`, data),
  remove:       (id) =>             api.delete(`/tasks/${id}`),
  overdueCount: () =>               api.get('/tasks/overdue/count'),
};

// System Status APIs
export const systemAPI = {
  getEmailStatus: () => api.get('/system/email-status'),
  getIntegrationSettings: () => api.get('/integration-settings'),
  updateIntegrationSettings: (data) => api.post('/integration-settings', data),
  testSmtp: () => api.post('/integration-settings/test-smtp'),
  testZohoSign: () => api.post('/integration-settings/test-zoho-sign'),
  getCompanySettings: () => api.get('/company-settings'),
  updateCompanySettings: (data) => api.put('/company-settings', data),
  // Branding image upload — kind is "logo" or "favicon". Multipart upload
  // to R2 via the backend; returns { url, kind, field }. The backend mirrors
  // the URL into both company_settings (in-app) AND platform tenants
  // (login page + BrandingContext) so it's live everywhere immediately.
  uploadBrandingImage: (file, kind) => {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('kind', kind);
    return api.post('/company-settings/upload-branding-image', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Default currency change. Super_admin only. First call without `confirm`
  // returns 409 with a confirmation_required payload + counts when records
  // exist; second call passes the new currency code as `confirm` to commit.
  // When zero records exist (fresh tenant), the first call succeeds directly.
  updateTenantCurrency: (currency, confirm) =>
    api.patch('/company-settings/currency', { currency, confirm: confirm || undefined }),
};

// Accounting APIs
// Country → State → City reference data for the standard lead location
// fields. Backed by the vendored dataset in backend/data/geo (no external
// service). Lists are suggestions — the City combobox allows custom entry.
export const geoAPI = {
  countries: () => api.get('/geo/countries'),
  states: (country) => api.get(`/geo/states?country=${encodeURIComponent(country)}`),
  cities: (country, state = '') =>
    api.get(`/geo/cities?country=${encodeURIComponent(country)}${state ? `&state=${encodeURIComponent(state)}` : ''}`),
};

export const accountingAPI = {
  // Dashboard
  getDashboard: (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const qs = params.toString();
    return api.get(`/accounting/dashboard${qs ? `?${qs}` : ''}`);
  },
  
  // Sales
  getSales: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/sales?${queryParams.toString()}`);
  },
  createSale: (data) => api.post('/accounting/sales', data),
  updateSale: (saleId, data) => api.put(`/accounting/sales/${saleId}`, data),
  deleteSale: (saleId) => api.delete(`/accounting/sales/${saleId}`),

  // Collections grouped by payment method (cash / bank / cheque / online /
  // each "Other" name). Verified collections only.
  getCollectionsByMethod: (params) => api.get('/accounting/reports/collections-by-method', { params }),

  // Payment verification queue
  getPendingVerifications: () => api.get('/accounting/sales/pending-verification'),
  // Cascade-FAILED sales — the accountant's inspect/retry queue.
  getFailedCascades: () => api.get('/accounting/sales/failed-cascades'),
  retryCascade: (saleId) => api.post(`/accounting/sales/${saleId}/retry-cascade`),
  // Accountant edits a still-pending sale's figures (deal value, amount
  // received, balance due date, method, reference) BEFORE confirming — the
  // deliberate, discoverable place to correct what the consultant entered.
  // Confirm stays a pure confirm.
  editPendingSale: (saleId, data = {}) => api.patch(`/accounting/sales/${saleId}/pending-edit`, data),
  confirmPayment: (saleId, data = {}) => api.post(`/accounting/sales/${saleId}/confirm-payment`, data),
  rejectPayment: (saleId, reason) => api.post(`/accounting/sales/${saleId}/reject-payment`, { reason }),
  getVerificationHistory: (saleId) => api.get(`/accounting/sales/${saleId}/verifications`),
  getSalesAnalytics: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/sales/analytics?${queryParams.toString()}`);
  },
  getSalesDistribution: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/sales/distribution?${queryParams.toString()}`);
  },
  
  // Expenses
  // `config` carries an AbortSignal so a page that unmounts mid-fetch cancels
  // its request instead of letting it land (and spend rate-limit budget).
  getExpenses: (params = {}, config = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/expenses?${queryParams.toString()}`, config);
  },
  // Recurring-expense templates (plan rows the actuals ledger excludes),
  // each with a materialization status explaining what posts when.
  getExpenseTemplates: () => api.get('/accounting/expenses/templates'),
  createExpense: (formData) => api.post('/accounting/expenses', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateExpense: (expenseId, formData) => api.put(`/accounting/expenses/${expenseId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteExpense: (expenseId) => api.delete(`/accounting/expenses/${expenseId}`),
  
  // Expense payment. The backend reads these as FORM fields — the old
  // query-string version silently bound nothing (even paid_date was ignored
  // and every payment recorded as bank_transfer).
  markExpensePaid: (expenseId, data = {}) => {
    const fd = new FormData();
    if (data.paid_date) fd.append('paid_date', data.paid_date);
    if (data.paid_amount != null && data.paid_amount !== '') fd.append('paid_amount', data.paid_amount);
    if (data.payment_method) fd.append('payment_method', data.payment_method);
    if (data.payment_reference) fd.append('payment_reference', data.payment_reference);
    return api.post(`/accounting/expenses/${expenseId}/mark-paid`, fd);
  },
  markExpenseUnpaid: (expenseId) => api.post(`/accounting/expenses/${expenseId}/mark-unpaid`),

  // Custom categories, parties, locks, audit, cash position (2026-07-21)
  createExpenseCategory: (name) => api.post('/accounting/expense-categories', { name }),
  getVendors: () => api.get('/accounting/vendors'),
  // Both ledgers now take the page's date window. getVendorLedger took none,
  // so it always returned every row ever regardless of the period on screen.
  getVendorLedger: (name, params) =>
    api.get('/accounting/vendors/ledger', { params: { name, ...(params || {}) } }),
  // Ledger by expense head, category or party — "all Rent & Maintenance this
  // year" was previously not askable at all.
  getExpenseLedger: (dimension, value, params) =>
    api.get('/accounting/expenses/ledger', { params: { dimension, value, ...(params || {}) } }),
  // Clear a recurring plan's end date and post whatever it missed.
  resumeRecurringPlan: (templateId, recurrence_end_date = null) =>
    api.post(`/accounting/expenses/templates/${encodeURIComponent(templateId)}/resume`,
             { recurrence_end_date }),
  getLocks: () => api.get('/accounting/locks'),
  lockMonth: (month) => api.post(`/accounting/locks/${month}`),
  unlockMonth: (month) => api.delete(`/accounting/locks/${month}`),
  getExpenseAudit: (expenseId) => api.get(`/accounting/expenses/${expenseId}/audit`),
  // Both now take the page's window. Without it these returned all-time
  // figures that were rendered under a period header (2026-08-21).
  getCashPosition: (params) => api.get('/accounting/reports/cash-position', { params }),
  getPayablesAging: () => api.get('/accounting/reports/payables-aging'),
  createCashAdjustment: (data) => api.post('/accounting/cash-adjustments', data),
  // Collections queue — outstanding balances as WORK, not as a report.
  // /reports/outstanding lists the same money with no owner, no chase history
  // and no next action, so nothing about it says what to do today.
  getCollectionsQueue: (params) => api.get('/accounting/collections/queue', { params }),
  logCollectionChase: (saleId, body) =>
    api.post(`/accounting/collections/${encodeURIComponent(saleId)}/chase`, body),
  getCollectionChases: (saleId) =>
    api.get(`/accounting/collections/${encodeURIComponent(saleId)}/chases`),
  getCashAdjustments: () => api.get('/accounting/cash-adjustments'),

  getMonthlyReport: (month) => {
    const [year, mon] = month.split('-');
    return api.get(`/accounting/reports/monthly?year=${year}&month=${parseInt(mon, 10)}`);
  },
  
  getExpenseAnalytics: (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    return api.get(`/accounting/expenses/analytics?${queryParams.toString()}`);
  },
  getExpenseCategories: () => api.get('/accounting/expense-categories'),
  
  // Forecast
  getForecast: (months = 3) => api.get(`/accounting/forecast?months=${months}`),
  
  // Reports
  getSalesReport: (startDate, endDate) => api.get(`/accounting/reports/sales?start_date=${startDate}&end_date=${endDate}`),
  getExpenseReport: (startDate, endDate) => api.get(`/accounting/reports/expenses?start_date=${startDate}&end_date=${endDate}`),
  getPnLReport: (startDate, endDate) => api.get(`/accounting/reports/pnl?start_date=${startDate}&end_date=${endDate}`),
  getOutstandingReport: (startDate, endDate) => api.get(`/accounting/reports/outstanding${startDate ? `?start_date=${startDate}&end_date=${endDate}` : ''}`),
  
  // Payment Sync
  syncPayment: (caseId, amountReceived, paymentDate) => 
    api.post(`/cases/${caseId}/sync-payment?amount_received=${amountReceived}${paymentDate ? `&payment_date=${paymentDate}` : ''}`),
};

// Sales Leads APIs
export const leadsAPI = {
  // Points of contact on a company lead (B2B Editions).
  listContacts: (leadId) => api.get(`/leads/${leadId}/contacts`),
  addContact: (leadId, data) => api.post(`/leads/${leadId}/contacts`, data),
  updateContact: (leadId, contactId, data) => api.patch(`/leads/${leadId}/contacts/${contactId}`, data),
  deleteContact: (leadId, contactId) => api.delete(`/leads/${leadId}/contacts/${contactId}`),
  getLeads: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))).toString();
    return api.get(`/leads${qs ? `?${qs}` : ''}`);
  },
  createLead: (data) => api.post('/leads', data),
  getLead: (id) => api.get(`/leads/${id}`),
  updateLead: (id, data) => api.put(`/leads/${id}`, data),
  updateStage: (id, data) => api.patch(`/leads/${id}/stage`, data),
  deleteLead: (id) => api.delete(`/leads/${id}`),
  assignLead: (id, consultantId) => api.patch(`/leads/${id}/assign?consultant_id=${consultantId}`),
  logCall: (id, data) => api.post(`/leads/${id}/calls`, data),
  // Unified activity entry — ONE save records the interaction (call/note),
  // optionally advances the stage (gate fields folded in), and optionally sets
  // the next follow-up. Kills the pipeline triple-entry.
  logActivity: (id, data) => api.post(`/leads/${id}/log-activity`, data),
  getCalls: (id) => api.get(`/leads/${id}/calls`),
  addNote: (id, content) => api.post(`/leads/${id}/notes`, { content }),
  getNotes: (id) => api.get(`/leads/${id}/notes`),
  getActivities: (id) => api.get(`/leads/${id}/activities`),
  // Cal.com bookings on this lead — from the website button or the voice agent.
  getAppointments: (id) => api.get(`/leads/${id}/appointments`),
  getEmails: (id) => api.get(`/leads/${id}/emails`),
  // Analytics
  getPipelineAnalytics: (params) => api.get('/leads/analytics/pipeline', { params }),
  getFunnelAnalytics: () => api.get('/leads/analytics/funnel'),
  getTrends: (days = 30) => api.get(`/leads/analytics/trends?days=${days}`),
  getConsultantPerformance: () => api.get('/leads/analytics/consultant-performance'),
  getSourceBreakdown: () => api.get('/leads/analytics/sources'),
  getAgingSummary: () => api.get('/leads/analytics/aging'),
  // Bulk operations
  // Phase 1.7g — reason is required on the backend when ANY of the
  // selected leads has an existing owner (= a real reassignment).
  // First-time routing accepts an empty reason.
  bulkAssign: (leadIds, consultantId, reason = '') =>
    api.post('/leads/bulk/assign', { lead_ids: leadIds, consultant_id: consultantId, reason }),
  // Phase 1.7g — lead governance reports
  deletionReport: (days = 90) =>
    api.get('/leads/reports/deletions', { params: { days } }),
  reassignmentReport: (days = 30) =>
    api.get('/leads/reports/reassignments', { params: { days } }),
  // Phase 1.8c — My Day panel endpoints. Accept the board's owner scope so
  // the tiles respect the consultant dropdown (assigned_to) and the
  // Team/Mine toggle (mine), exactly like the main board fetch does.
  overdueFollowUps:  (params = {}) => api.get('/leads/follow-ups/due', { params }),
  todayFollowUps:    (params = {}) => api.get('/leads/follow-ups/today', { params }),
  upcomingWeekFollowUps: (params = {}) => api.get('/leads/follow-ups/upcoming-week', { params }),
  // "No next step" leak detector — active-stage leads with no scheduled
  // follow-up, untouched >= `days` business days (default 3). Consultant sees
  // own; manager/admin sees the team + a per-consultant breakdown.
  noNextStepLeads:   (days) => api.get('/leads/follow-ups/no-next-step', { params: days ? { days } : {} }),
  // Salesforce-style activity log on Done. `body` is the FollowUpDoneBody
  // shape: { outcome, notes, no_further_followup, next_follow_up_date,
  // next_follow_up_type }. Backend rejects silent completions — every
  // call must include outcome + notes + (next OR no_further).
  followUpDone:      (leadId, body) =>
    api.post(`/leads/${leadId}/follow-up/done`, body),
  // Snooze the REMINDER, not the commitment. `snoozeUntil` is ISO datetime
  // when the My Day nudge should reappear. Lead's follow_up_date is
  // unchanged — that's a Done → next-followup flow.
  followUpSnooze:    (leadId, snoozeUntil) =>
    api.post(`/leads/${leadId}/follow-up/snooze`, { snooze_until: snoozeUntil }),
  // Universal lead search with Salesforce-style deconfliction. Returns
  // { own: [...], others: [...stub] }. `own` are full detail (the
  // consultant's portfolio OR everything for manager+); `others` are
  // stubs (anonymized name + stage + owner) so the rep can see a lead
  // is in the pipeline without poaching contact details.
  searchLeads:       (q, limit = 10) =>
    api.get('/leads/search', { params: { q, limit } }),
  bulkStage: (leadIds, stage) => api.post('/leads/bulk/stage', { lead_ids: leadIds, stage }),
  // Follow-ups
  getOverdueFollowups: () => api.get('/leads/follow-ups/due'),
  getTodayFollowups: () => api.get('/leads/follow-ups/today'),
  // My Day command bar — counts + the deterministic "start working" lead pointer
  getTodaySummary: () => api.get('/leads/today-summary'),
  // Lead Sources
  getSources: () => api.get('/leads/sources'),
  createSource: (data) => api.post('/leads/sources', data),
  deleteSource: (id) => api.delete(`/leads/sources/${id}`),
  // Sales Module Settings
  getSettings: () => api.get('/leads/settings'),
  updateSettings: (data) => api.put('/leads/settings', data),
  // AI Insights — these hit Claude and can be slow; cap them so a stuck model
  // call fails gracefully (the section just doesn't render) instead of leaving
  // a request hanging indefinitely.
  getAIBestLeads:     () => api.get('/leads/ai/best-leads', { timeout: 45000 }),
  getAIInsights:      () => api.get('/leads/ai/insights', { timeout: 45000 }),
  // Takes the page's window since 2026-08-21. Without it this returned
  // all-time figures rendered beside period-filtered tiles, so Lead Source
  // ROI reported 1,117 leads on a page headed 343.
  getAIPipelineHealth: (params) =>
    api.get('/leads/ai/pipeline-health', { params, timeout: 45000 }),
  // Admin one-off (idempotent): stamp deal_value + balance_due on existing
  // registered leads from their sale + clear stale fully-paid follow-ups.
  backfillRevenue: () => api.post('/leads/backfill-revenue'),
  // Collapse-to-one duplicate merge: survivor stays active + assigned; the
  // merged enquiries are archived (kept for history). { survivor_id, merge_ids, assigned_to }
  merge: (body) => api.post('/leads/merge', body),
  // Merge history + undo. Both live under /duplicates/ because a
  // single-segment GET on the leads router is swallowed by /{lead_id}.
  listMerges: (days = 90) => api.get(`/leads/duplicates/merges?days=${days}`),
  unmerge: (body) => api.post('/leads/duplicates/unmerge', body),
  // "Not a duplicate" — dismiss a (usually fuzzy) duplicate flag.
  unflagDuplicate: (id) => api.post(`/leads/${id}/unflag-duplicate`),
};

// Marketing APIs
export const marketingAPI = {
  // Stage-1: scan only (no import), returns duplicate analysis
  scanLeads: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/marketing/leads/scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Quick import (auto-marks duplicates, no per-row decisions)
  importLeads: (formData) => api.post('/marketing/leads/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Stage-3: import with per-row decisions (import/skip/merge)
  importReviewed: (data) => api.post('/marketing/leads/import-reviewed', data),
  bulkAssign: (data) => api.post('/marketing/leads/bulk-assign', data),
  getImports: () => api.get('/marketing/imports'),
  getImport: (id) => api.get(`/marketing/imports/${id}`),
  deleteImport: (id) => api.delete(`/marketing/imports/${id}`),
  getLeadPool: (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))).toString();
    return api.get(`/marketing/pool${qs ? `?${qs}` : ''}`);
  },
  assignFromPool: (leadIds, consultantId) => api.post('/marketing/leads/bulk-assign', { lead_ids: leadIds, consultant_id: consultantId }),
  getDedupPolicy: () => api.get('/marketing/dedup-policy'),
  updateDedupPolicy: (p) => api.put('/marketing/dedup-policy', p),
  getAnalytics: () => api.get('/marketing/analytics'),
  getDuplicateAnalytics: () => api.get('/marketing/duplicates/analytics'),
  getConsultants: () => api.get('/marketing/consultants'),
  getDuplicateGroup: (groupId) => api.get(`/marketing/duplicates/group/${groupId}`),
  getLeadEnquiryHistory: (leadId) => api.get(`/marketing/duplicates/lead/${leadId}`),
  // Phase 1.10b — Meta Ads performance (spend↔conversion join per campaign).
  metaPerformance: (days = 30) => api.get(`/marketing/meta-performance?days=${days}`),
};

// Attendance APIs
export const attendanceAPI = {
  punchIn: (lateReason = '') => api.post(`/attendance/punch-in${lateReason ? `?late_reason=${encodeURIComponent(lateReason)}` : ''}`),
  punchOut: (notes = '') => api.post(`/attendance/punch-out${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`),
  breakStart: (reason = '') => api.post(`/attendance/break-start${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`),
  breakEnd: () => api.post('/attendance/break-end'),
  getToday: () => api.get('/attendance/today'),
  getHistory: (limit = 30) => api.get(`/attendance/history?limit=${limit}`),
  getTeam: () => api.get('/attendance/team'),
  // Per-day ledger for one staff member, one period (YYYY-MM)
  getLedger: (period, userId) =>
    api.get(`/attendance/ledger/${period}${userId ? `?user_id=${userId}` : ''}`),
};

// HR Policy + Holidays + Leaves
export const hrAPI = {
  // Policy
  getPolicy:      ()                  => api.get('/hr/policy'),
  updatePolicy:   (policy)            => api.put('/hr/policy', policy),
  // Holidays
  listHolidays:   (year)              => api.get(`/hr/holidays${year ? `?year=${year}` : ''}`),
  addHoliday:     (data)              => api.post('/hr/holidays', data),
  deleteHoliday:  (id)                => api.delete(`/hr/holidays/${id}`),
  // Leaves
  listLeaves:     (params = {})       => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return api.get(`/hr/leaves${qs ? `?${qs}` : ''}`);
  },
  requestLeave:   (data)              => api.post('/hr/leaves', data),
  approveLeave:   (id, note)          => api.patch(`/hr/leaves/${id}/approve${note ? `?note=${encodeURIComponent(note)}` : ''}`),
  rejectLeave:    (id, note)          => api.patch(`/hr/leaves/${id}/reject${note ? `?note=${encodeURIComponent(note)}` : ''}`),
  cancelLeave:    (id)                => api.delete(`/hr/leaves/${id}`),
  getLeaveBalance:(userId, year)      => api.get(`/hr/leaves/balance/${userId}${year ? `?year=${year}` : ''}`),
  // Manual attendance ledger overrides
  setLedgerOverride:   (data)         => api.post('/attendance/ledger-overrides', data),
  clearLedgerOverride: (userId, date) => api.delete(`/attendance/ledger-overrides?user_id=${userId}&date=${date}`),
};

// Payroll preview / run / lock / mark-paid
export const payrollAPI = {
  preview:       (period)         => api.get(`/payroll/preview/${period}`),
  // opts.allowEarly — approve a month that has not ended yet (the server
  // refuses without it). opts.recompute — rebuild an approved run from the
  // current attendance ledger. Both default off; existing callers pass
  // nothing and behave exactly as before.
  run:           (period, opts = {}) => api.post(`/payroll/run/${period}`, null, {
    params: {
      ...(opts.allowEarly ? { allow_early: true } : {}),
      ...(opts.recompute  ? { recompute: true }   : {}),
    },
  }),
  lock:          (runId)          => api.post(`/payroll/runs/${runId}/lock`),
  listRuns:      ()               => api.get('/payroll/runs'),
  getRun:        (runId)          => api.get(`/payroll/runs/${runId}`),
  markLinePaid:  (runId, lineId)  => api.post(`/payroll/runs/${runId}/lines/${lineId}/mark-paid`),
};

// Document Library APIs
export const libraryAPI = {
  getCategories: () => api.get('/library/categories'),
  createCategory: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    return api.post('/library/categories', formData);
  },
  deleteCategory: (categoryId) => api.delete(`/library/categories/${categoryId}`),
  getDocuments: (categoryId) => api.get(`/library/documents${categoryId ? `?category_id=${categoryId}` : ''}`),
  uploadDocument: (data) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('description', data.description || '');
    formData.append('category_id', data.category_id);
    formData.append('file', data.file);
    return api.post('/library/documents', formData);
  },
  deleteDocument: (documentId) => api.delete(`/library/documents/${documentId}`),
  shareDocument: (documentId, caseId) => {
    const formData = new FormData();
    formData.append('case_id', caseId);
    return api.post(`/library/documents/${documentId}/share`, formData);
  },
};

// AI Workforce — interactions, lead pool, availability
export const aiInteractionsAPI = {
  get: (interactionId) => api.get(`/sso/ai-interactions/${interactionId}`),
};

export const webFormsAPI = {
  list:        (includeArchived) => api.get('/web-forms', { params: includeArchived ? { include_archived: true } : {} }),
  get:         (id)              => api.get(`/web-forms/${id}`),
  create:      (body)            => api.post('/web-forms', body),
  update:      (id, body)        => api.put(`/web-forms/${id}`, body),
  publish:     (id)              => api.post(`/web-forms/${id}/publish`),
  unpublish:   (id)              => api.post(`/web-forms/${id}/unpublish`),
  archive:     (id)              => api.delete(`/web-forms/${id}`),
  leads:       (id)              => api.get(`/web-forms/${id}/leads`),
  sourceInsights: (days)         => api.get('/web-forms/insights/sources', { params: days ? { days } : {} }),
};

// Demo domain switch (staging only — the backend answers `enabled: false`
// wherever DEMO_DOMAIN_SWITCH is unset, which is everywhere in production).
export const demoDomainsAPI = {
  list:   ()     => api.get('/demo/domains'),
  switch: (slug) => api.post('/demo/switch', { slug }),
};

export const leadPoolAPI = {
  list:    ()                  => api.get('/lead-pool'),
  claim:   (leadId)            => api.post(`/lead-pool/${leadId}/claim`),
  assign:  (leadId, userId)    => api.post(`/lead-pool/${leadId}/assign`, { user_id: userId }),
  satisfy: (leadId)            => api.post(`/lead-pool/${leadId}/satisfy`),
};

// Phase 1.8m — discount policy + approval inbox
export const discountAPI = {
  getPolicy: ()       => api.get('/discount/policy'),
  updatePolicy: (p)   => api.put('/discount/policy', p),
  pendingApprovals: (assignedToMe = false) =>
    api.get('/discount/pending-approvals', { params: assignedToMe ? { assigned_to_me: true } : {} }),
  approve: (id, reason = '') => api.post(`/discount/approvals/${id}/approve`, { reason }),
  reject:  (id, reason)      => api.post(`/discount/approvals/${id}/reject`,  { reason }),
  audit:   (days = 90)       => api.get('/discount/audit', { params: { days } }),
};

export const availabilityAPI = {
  getMine:   ()        => api.get('/availability/me'),
  setMine:   (status)  => api.post('/availability/me', { status }),
  online:    (role)    => api.get('/availability/online', { params: role ? { role } : {} }),
};

// Phase 1.7 — cascade-aware lifecycle (soft delete + Recycle Bin).
// Always go through `DeleteConfirmModal` from
// components/lifecycle/DeleteConfirmModal.js so the audit reason is
// captured and the type-to-confirm guard fires.
export const lifecycleAPI = {
  preview: (recordType, recordId) =>
    api.get(`/lifecycle/preview/${recordType}/${recordId}`),
  delete: ({ record_type, record_id, reason, force = false }) =>
    api.post(`/lifecycle/delete`, { record_type, record_id, reason, force }),

  // Recycle Bin (admin-only)
  recycleBin: (recordType) =>
    api.get('/lifecycle/recycle-bin', { params: recordType ? { record_type: recordType } : {} }),
  restore: ({ record_type, record_id }) =>
    api.post('/lifecycle/restore', { record_type, record_id }),
  purge: ({ record_type, record_id }) =>
    api.post('/lifecycle/purge', { record_type, record_id }),
};

// Phase 2 — payment gateway integrations (Razorpay + Stripe).
// Settings → Integrations → Payment Gateways. Admin-only on the backend.
// The decrypted credentials never cross this API; only masked previews
// + connection status return to the UI.
export const integrationsAPI = {
  list: () => api.get('/integrations'),
  testRazorpay: (payload) => api.post('/integrations/razorpay/test', payload),
  connectRazorpay: (payload) => api.post('/integrations/razorpay/connect', payload),
  disconnectRazorpay: () => api.delete('/integrations/razorpay'),
  testStripe: (payload) => api.post('/integrations/stripe/test', payload),
  connectStripe: (payload) => api.post('/integrations/stripe/connect', payload),
  disconnectStripe: () => api.delete('/integrations/stripe'),
  setCaptureMode: (mode) =>
    api.patch('/integrations/payment-capture-mode', { payment_capture_mode: mode }),
  // Phase 2.5 — tax configuration (master toggle + country routing).
  getTaxConfiguration: () => api.get('/integrations/tax-configuration'),
  putTaxConfiguration: (payload) => api.put('/integrations/tax-configuration', payload),
  // Live tax breakdown for a given amount (RegisterLeadModal). Same engine as
  // the sale/invoice — single source of truth. { amount, client_country?, client_state? }
  taxPreview: (payload) => api.post('/integrations/tax-preview', payload),
};

// Phase 1.10b — Meta (Facebook/Instagram) Lead Ads connect.
// Settings → Integrations → Meta Ads. Admin-only on the backend; access
// tokens never cross this API (status/assets are metadata only).
export const metaAdsAPI = {
  status: () => api.get('/integrations/meta/status'),
  oauthStart: () => api.post('/integrations/meta/oauth/start'),
  assets: () => api.get('/integrations/meta/assets'),
  select: (payload) => api.post('/integrations/meta/select', payload),
  disconnect: () => api.delete('/integrations/meta'),
};

// Settings → Integrations → Website Analytics (Acquisition Phase D).
// GA4 + Search Console via a per-tenant read-only Google service account.
// Admin-only on the backend; the key never comes back (status is metadata).
// `source` is 'ga4' | 'gsc'.
// Marketing → Acquisition (Acquisition Phase G/H). Read-only, MARKETING_ROLES.
export const acquisitionAPI = {
  status: () => api.get('/marketing/acquisition/status'),
  overview: (params) => api.get('/marketing/acquisition/overview', { params }),
  traffic: (params) => api.get('/marketing/acquisition/traffic', { params }),
  search: (params) => api.get('/marketing/acquisition/search', { params }),
  landingPages: (params) => api.get('/marketing/acquisition/landing-pages', { params }),
  leads: (params) => api.get('/marketing/acquisition/leads', { params }),
  attribution: (params) => api.get('/marketing/acquisition/attribution', { params }),
};

export const googleIntegrationAPI = {
  status: () => api.get('/integrations/google/status'),
  test: (source, payload) => api.post(`/integrations/google/${source}/test`, payload || {}),
  connect: (source, payload) => api.post(`/integrations/google/${source}/connect`, payload),
  syncNow: (source, days) => api.post(`/integrations/google/${source}/sync`, null, { params: days ? { days } : {} }),
  disconnect: (source) => api.delete(`/integrations/google/${source}`),
};

// Growth Intelligence K0 — privacy / consent (tenant admin).
export const privacyAPI = {
  settings: () => api.get('/privacy/settings'),
  updateSettings: (body) => api.put('/privacy/settings', body),
  retention: () => api.get('/privacy/retention'),
  summary: () => api.get('/privacy/summary'),
  suppressions: () => api.get('/privacy/suppressions'),
  addSuppression: (email, reason) => api.post('/privacy/suppressions', { email, reason }),
  eligibility: (email) => api.post('/privacy/eligibility', { email }),
  purge: (body) => api.post('/privacy/dsar/purge', { email: body.email || null, client_ref: body.client_ref || null }),
};

// Growth AA — Technical SEO. Marketing roles read; only admins crawl or configure.
export const techSeoAPI = {
  report: () => api.get('/marketing/seo/tech'),
  pages: (params) => api.get('/marketing/seo/tech/pages', { params }),
  crawl: () => api.post('/marketing/seo/tech/crawl'),
  config: () => api.get('/marketing/seo/tech/config'),
  setConfig: (body) => api.put('/marketing/seo/tech/config', body),
};

// Growth AB — Keyword Intelligence. Marketing roles read; only admins run or configure.
export const keywordsAPI = {
  report: (params) => api.get('/marketing/keywords', { params }),
  config: () => api.get('/marketing/keywords/config'),
  setConfig: (body) => api.put('/marketing/keywords/config', body),
  run: (params) => api.post('/marketing/keywords/run', null, { params }),
};

// Growth AC — Competitor Intelligence. Mirrors routers/competitor_intelligence.py.
export const competitorsAPI = {
  report: (params) => api.get('/marketing/competitors', { params }),
  config: () => api.get('/marketing/competitors/config'),
  setConfig: (body) => api.put('/marketing/competitors/config', body),
  run: (params) => api.post('/marketing/competitors/run', null, { params }),
  providers: (params) => api.get('/marketing/competitors/providers', { params }),
};

// Growth AD — Gap Intelligence. Mirrors routers/keyword_gap_engine.py.
export const gapsAPI = {
  report: (params) => api.get('/marketing/gaps', { params }),
  config: () => api.get('/marketing/gaps/config'),
  setConfig: (body) => api.put('/marketing/gaps/config', body),
  run: () => api.post('/marketing/gaps/run'),
};

// Growth AE — Authority Intelligence. Mirrors routers/authority_intelligence.py.
export const authorityAPI = {
  report: (params) => api.get('/marketing/authority', { params }),
  config: () => api.get('/marketing/authority/config'),
  setConfig: (body) => api.put('/marketing/authority/config', body),
  run: () => api.post('/marketing/authority/run'),
  providers: (params) => api.get('/marketing/authority/providers', { params }),
};

// Growth AF — Content Intelligence. Mirrors routers/content_intelligence.py.
export const contentAPI = {
  report: (params) => api.get('/marketing/content', { params }),
  config: () => api.get('/marketing/content/config'),
  setConfig: (body) => api.put('/marketing/content/config', body),
  run: () => api.post('/marketing/content/run'),
};

// Growth AG — GEO & AI Visibility. Mirrors routers/ai_visibility.py.
export const aiVisibilityAPI = {
  report: (params) => api.get('/marketing/geo', { params }),
  config: () => api.get('/marketing/geo/config'),
  setConfig: (body) => api.put('/marketing/geo/config', body),
  run: () => api.post('/marketing/geo/run'),
  providers: (params) => api.get('/marketing/geo/providers', { params }),
};

// Growth Engine — the loop's own API (CC-0 substrate: brain · goal · plan · policy · outcomes).
// The Command Center reads these; it computes nothing of its own. Every action is admin-gated server-side.
export const engineAPI = {
  brain:             ()            => api.get('/marketing/engine/brain'),
  questions:         (params)      => api.get('/marketing/engine/questions', { params }),
  next:              ()            => api.get('/marketing/engine/next'),
  plan:              (params)      => api.get('/marketing/engine/plan', { params }),
  opportunities:     (params)      => api.get('/marketing/engine/opportunities', { params }),
  setStatus:         (id, body)    => api.post(`/marketing/engine/opportunities/${id}/status`, body),
  act:               (id, body)    => api.post(`/marketing/engine/opportunities/${id}/act`, body),
  goals:             (params)      => api.get('/marketing/engine/goals', { params }),
  goalMetrics:       ()            => api.get('/marketing/engine/goals/metrics'),
  createGoal:        (body)        => api.post('/marketing/engine/goals', body),
  updateGoal:        (id, body)    => api.patch(`/marketing/engine/goals/${id}`, body),
  rebaselineGoal:    (id, reason)  => api.post(`/marketing/engine/goals/${id}/rebaseline`, { reason }),
  goalMarkets:       ()            => api.get('/marketing/engine/goals/markets'),
  goalOpportunities: (id, params)  => api.get(`/marketing/engine/goals/${id}/opportunities`, { params }),
  outcomes:          (params)      => api.get('/marketing/engine/outcomes', { params }),
  runs:              (params)      => api.get('/marketing/engine/runs', { params }),
  run:               (only)        => api.post('/marketing/engine/run', only || null),
  policy:            ()            => api.get('/marketing/engine/policy'),
  setPolicy:         (body)        => api.put('/marketing/engine/policy', body),
  kill:              (reason)      => api.post('/marketing/engine/policy/kill', { reason: reason || null }),
  approvals:         (params)      => api.get('/marketing/engine/approvals', { params }),
  approve:           (id)          => api.post(`/marketing/engine/approvals/${id}/approve`),
  reject:            (id, note)    => api.post(`/marketing/engine/approvals/${id}/reject`, { note: note || null }),
  sources:           ()            => api.get('/marketing/engine/sources'),
};

// Growth AI — Content Production (own router /marketing/content-production; read for Growth roles, actions admin-only).
// The page arrives in Phase D; this client is the contract it will use.
export const contentProductionAPI = {
  report: (params) => api.get('/marketing/content-production', { params }),
  recommendations: (params) => api.get('/marketing/content-production/recommendations', { params }),
  recommendation: (afId) => api.get(`/marketing/content-production/recommendations/${afId}`),
  create: (afId) => api.post('/marketing/content-production/items', { af_opportunity_id: afId }),
  item: (id) => api.get(`/marketing/content-production/items/${id}`),
  itemRecommendation: (id) => api.get(`/marketing/content-production/items/${id}/recommendation`),
  generate: (id, instruction) => api.post(`/marketing/content-production/items/${id}/generate`, { instruction: instruction || null }),
  edit: (id, body) => api.put(`/marketing/content-production/items/${id}/draft`, body),
  submitForReview: (id, note) => api.post(`/marketing/content-production/items/${id}/submit-for-review`, { note: note || null }),
  approve: (id, note) => api.post(`/marketing/content-production/items/${id}/approve`, { note: note || null }),
  reject: (id, note) => api.post(`/marketing/content-production/items/${id}/reject`, { note: note || null }),
  pause: (id, note) => api.post(`/marketing/content-production/items/${id}/pause`, { note: note || null }),
  resume: (id) => api.post(`/marketing/content-production/items/${id}/resume`),
  preparePublish: (id) => api.post(`/marketing/content-production/items/${id}/prepare-publish`),
  versions: (id) => api.get(`/marketing/content-production/items/${id}/versions`),
  version: (id, n) => api.get(`/marketing/content-production/items/${id}/versions/${n}`),
  provenance: (id) => api.get(`/marketing/content-production/items/${id}/provenance`),
  author: (id, body) => api.post(`/marketing/content-production/items/${id}/author`, body),
  recordPublication: (id, body) => api.post(`/marketing/content-production/items/${id}/record-publication`, body),
  measure: (id, days) => api.post(`/marketing/content-production/items/${id}/measure`, null, { params: days ? { days } : undefined }),
  artifact: (id) => api.get(`/marketing/content-production/items/${id}/artifact`),
  budget: () => api.get('/marketing/content-production/budget'),
  config: () => api.get('/marketing/content-production/config'),
  setConfig: (body) => api.put('/marketing/content-production/config', body),
};

// Growth AH — Brand Intelligence (own router /marketing/brand; read for Growth roles, run/config admin-only).
export const brandAPI = {
  report: (params) => api.get('/marketing/brand', { params }),
  config: () => api.get('/marketing/brand/config'),
  setConfig: (body) => api.put('/marketing/brand/config', body),
  run: () => api.post('/marketing/brand/run'),
  sources: () => api.get('/marketing/brand/sources'),
};

// Autopilot Intelligence Layer (Phase 3, Layers 0-2 + Ask Euphoric).
// Roles: super_admin / admin / process_manager — mirror of the backend
// AUTOPILOT_ROLES list; keep the two in lockstep (permission-flow gate).
// Balance write-offs — admin/super_admin execute, accountant requests,
// admins approve/reject; ledger is append-only server-side.
export const writeOffsAPI = {
  create:  (saleId, reason) => api.post(`/write-offs/sales/${saleId}`, { reason }),
  approve: (requestId) => api.post(`/write-offs/${requestId}/approve`),
  reject:  (requestId, note) => api.post(`/write-offs/${requestId}/reject`, { note }),
  // params: { start_date, end_date } — a write-off is dated by WHEN THE
  // DECISION WAS MADE, so the accounting dashboard's period filter applies to
  // it the same way it applies to a payment.
  list:    (params) => api.get('/write-offs', { params }),
};

export const intelligenceAPI = {
  // `config` carries an AbortSignal so leaving the page cancels these instead
  // of letting them land (and spend rate-limit budget) on an unmounted view.
  health: (config = {}) => api.get('/intelligence/health', config),
  signals: (params, config = {}) => api.get('/intelligence/signals', { params, ...config }),
  acknowledgeSignal: (id) => api.post(`/intelligence/signals/${id}/acknowledge`),
  runMonitor: () => api.post('/intelligence/run'),
  standards: () => api.get('/intelligence/standards'),
  setStandard: (metric, value) => api.put(`/intelligence/standards/${metric}`, { value }),
  timeline: (entityType, entityId, params) =>
    api.get(`/intelligence/timeline/${entityType}/${entityId}`, { params }),
  ask: (question, requestId) => api.post('/intelligence/ask', { question, request_id: requestId }),
  // Streamed ask. Uses fetch rather than axios because axios cannot expose a
  // ReadableStream in the browser. `requestId` is generated by the CALLER
  // before sending — that is what makes a refresh free: the same id re-attaches
  // to the run already in flight instead of paying for a second one.
  askStream: (question, requestId, { onPhase, onProposal, onClarify, onAnswer, onError, signal } = {}) => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/intelligence/ask/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ question, request_id: requestId }),
      signal,
    }).then(async (res) => {
      if (!res.ok || !res.body) throw new Error(`stream failed: ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // SSE frames are separated by a blank line and can be split across
      // network chunks — parsing per-chunk would drop or corrupt events.
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (!frame.startsWith('data:')) continue;   // ": keep-alive" comments
          let evt;
          try { evt = JSON.parse(frame.slice(5).trim()); } catch { continue; }
          if (evt.type === 'phase') onPhase?.(evt);
          else if (evt.type === 'proposal') onProposal?.(evt);
          else if (evt.type === 'clarify') onClarify?.(evt);
          else if (evt.type === 'error') onError?.(evt);
          else if (evt.type === 'answer') onAnswer?.(evt);
        }
      }
    });
  },
  activeAskRun: (config = {}) => api.get('/intelligence/ask/runs/active', config),
  approveProposal: (proposalId) => api.post('/intelligence/ask/approve', { proposal_id: proposalId }),
  dismissProposal: (proposalId) => api.post('/intelligence/ask/dismiss', { proposal_id: proposalId }),
  askCapabilities: () => api.get('/intelligence/ask/capabilities'),
  briefingToday: (config = {}) => api.get('/intelligence/briefing/today', config),
  weeklyStory: (config = {}) => api.get('/intelligence/weekly-story/latest', config),
  briefingAck: () => api.post('/intelligence/briefing/ack'),
  getAskPolicies: () => api.get('/intelligence/ask-policies'),
  putAskPolicies: (policies) => api.put('/intelligence/ask-policies', { policies }),
};
